import * as React from 'react';
import { Text, Flex, Box, Card, CardBody, FormControl, FormLabel, FormHelperText, Checkbox } from '@chakra-ui/react';
import placeholderExample from '../../../../assets/images/sample-atas-certificate.jpg';
import {
  UploadButtonSection,
  UploadActions,
  UploadExample,
  UploadComplete as UploadCompleteSection,
  AcknowledgeBox,
  ModalColumns
} from 'shared-components';
import { Link } from 'shared-components';

interface IProps {
  docKey: string;
  homeCountry: string;
  courseCAH3Code: string;
  courseEndDate: string;
  complete: boolean;
  fileNames: string[];
  onClose: () => void;
  onContinue: () => void;
  onTickATASCheckbox: (isATASPending: boolean) => void;
  onUpload: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle: string,
    fileNames: string[]
  ) => void;
  isAtasPending?: boolean;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
}

const ATASModal: React.FC<IProps> = (props: IProps) => {
  const { courseCAH3Code, courseEndDate, infoOnly, docStatus, docKey } = props;
  const isAtasTicked = props.isAtasPending || false;

  const handleUpload = () => {
    props.onUpload(null, null, 'atas', props.docKey, 'ATAS Certificate', props.fileNames);
  };

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  const CAH3CodeList = courseCAH3Code.split(',').map(item => {
    return <b>{item}</b>;
  });

  const courseEndDateList = courseEndDate.split(',').map(item => {
    return <b>{item}</b>;
  });

  const ATASIntro = () => (
    <>
      <Text>
        Your course requires that you apply for an ATAS certificate. Your ATAS application will ask for the following
        information:
      </Text>

      <Card variant={'filled'} my={6}>
        <CardBody>
          <Flex flexDirection={{ base: 'column', md: 'row' }}>
            <Box minWidth="12rem">Your CAH3 code:</Box>
            <Box>{CAH3CodeList}</Box>
          </Flex>

          {courseEndDate ? (
            <Flex flexDirection={{ base: 'column', md: 'row' }} mt={4}>
              <Box minWidth="12rem">Your course end date:</Box>
              <Box>{courseEndDateList}</Box>
            </Flex>
          ) : null}
        </CardBody>
      </Card>

      <Text>You can apply at the link below:</Text>

      <Text>
        <Link href="https://www.gov.uk/guidance/academic-technology-approval-scheme" isExternal>
          Apply for an ATAS certificate
        </Link>
      </Text>

      {infoOnly ? <Text>You may be asked for a copy of this certificate as part of your visa application.</Text> : null}
    </>
  );

  return (
    <ModalColumns
      id={'atas'}
      title={'ATAS Certificate'}
      onClose={handleOnClose}
      supportingColumn={<UploadExample altText="ATAS certificate example" url={placeholderExample} />}
    >
      {infoOnly ? (
        <>
          <ATASIntro />

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
            label="Return to document upload"
          />
        </>
      ) : (
        <>
          {props.complete ? (
            <UploadCompleteSection fileNames={props.fileNames} onUpload={handleUpload} onContinue={props.onContinue} />
          ) : (
            <>
              <ATASIntro />

              <FormControl data-selector="form-field" my={6}>
                <FormLabel data-selector="label" fontSize="lg">
                  Have you applied for your ATAS Certificate?
                </FormLabel>

                <FormHelperText mb={3}>Tick the box below after you apply</FormHelperText>

                <Checkbox
                  data-selector={`checkbox-container`}
                  isChecked={isAtasTicked}
                  onChange={e => {
                    props.onTickATASCheckbox(e.target.checked);
                  }}
                  variant="outline"
                  size="lg"
                >
                  I have applied
                </Checkbox>
              </FormControl>

              {isAtasTicked ? (
                <>
                  <Text>Once you have received your certificate, please upload it below:</Text>

                  <UploadButtonSection onUpload={handleUpload}>
                    Awaiting upload of <b>ATAS certificate</b>
                  </UploadButtonSection>
                </>
              ) : null}

              <UploadActions complete={props.complete} onContinue={props.onContinue} />
            </>
          )}
        </>
      )}
    </ModalColumns>
  );
};

export default ATASModal;
